import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IPartAspect } from "../../models/EbayOffer";
import useEbayAspects from "../../data/useEbayAspects";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";

interface JobPartAspectsProps {
  partDataId: string;
  ebayCategoryId: string;
  dismantlerAspectNames: string[];
  // The category's custom aspects; the part may not carry its own copy yet.
  categoryCustomAspects: IPartAspect[];
  existingAspects: IPartAspect[];
  onSaved?: () => void;
}

// One field on the card, whether it came from eBay's taxonomy or the category's custom aspects.
interface IDismantlerField {
  name: string;
  options: { localizedValue: string }[];
  isMulti: boolean;
  isRequired: boolean;
  // Used to create the part's row the first time it's answered.
  newAspect: Partial<IPartAspect>;
}

export default function JobPartAspects({
  partDataId,
  ebayCategoryId,
  dismantlerAspectNames,
  categoryCustomAspects,
  existingAspects,
  onSaved,
}: JobPartAspectsProps) {
  const { t } = useTranslation("jobs");
  const { saveHandlers: aspectSaveHandlers } = useDefaultCRUDHandlers(t("Aspect"));
  const { createUpdate: createUpdateAspects } = usePartAspectsMutations();
  const { aspects: ebayAspects } = useEbayAspects(ebayCategoryId);
  const { register } = useForm();

  const ebayFields: IDismantlerField[] = (ebayAspects.data ?? [])
    .filter((aspect) => dismantlerAspectNames.includes(aspect.localizedAspectName))
    .map((aspect) => ({
      name: aspect.localizedAspectName,
      options: aspect.aspectValues ?? [],
      isMulti: aspect.aspectConstraint.itemToAspectCardinality === "MULTI",
      isRequired: aspect.aspectConstraint.aspectRequired,
      newAspect: { name: aspect.localizedAspectName, isCustom: false },
    }));

  // A custom aspect sharing an eBay aspect's name is the same field, so it's only shown once.
  const customFields: IDismantlerField[] = categoryCustomAspects
    .filter((aspect) => dismantlerAspectNames.includes(aspect.name))
    .filter((aspect) => !ebayFields.some((field) => field.name === aspect.name))
    .map((aspect) => ({
      name: aspect.name,
      options: (aspect.options ?? "")
        .split(",")
        .map((option) => option.trim())
        .filter((option) => option.length > 0)
        .map((option) => ({ localizedValue: option })),
      isMulti: false,
      isRequired: aspect.isRequired,
      newAspect: {
        name: aspect.name,
        isCustom: true,
        isFromCategory: true,
        options: aspect.options,
        isRequired: aspect.isRequired,
      },
    }));

  const dismantlerFields = [...ebayFields, ...customFields];

  if (ebayAspects.isInitialLoading) {
    return (
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <p className="text-sm font-semibold text-gray-700">{t("Part Specifics")}</p>
        <div className="flex flex-wrap gap-y-2 gap-x-4 animate-pulse">
          {dismantlerAspectNames.map((name) => (
            <div key={name} className="flex flex-col gap-1">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-8 w-52 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (dismantlerFields.length === 0) return null;

  const getAspectValue = (aspectName: string) => {
    return existingAspects.find((aspect) => aspect.name === aspectName)?.value ?? "";
  };

  const saveAspectValue = (field: IDismantlerField, value: string) => {
    const existingAspect = existingAspects.find(
      (partAspect) => partAspect.name === field.name
    );
    const existingValue = existingAspect?.value ?? "";

    if (existingValue === value) return;
    if (!value && !existingValue) return;

    const body = {
      partDataId,
      aspects: [
        existingAspect
          ? { ...existingAspect, value }
          : { ...field.newAspect, value }
      ]
    };

    createUpdateAspects.mutate(body, {
      ...aspectSaveHandlers,
      onSuccess: () => {
        onSaved?.();
        aspectSaveHandlers.onSuccess();
      },
      onError: () => {
        aspectSaveHandlers.onError();
      }
    });
  };

  return (
    <div className="border border-gray-200 rounded p-3 space-y-2">
      <p className="text-sm font-semibold text-gray-700">{t("Part Specifics")}</p>
      <div className="flex flex-wrap gap-y-2 gap-x-4">
        {dismantlerFields.map((field) => {
          const aspectValue = getAspectValue(field.name);
          const hasOptions = field.options.length > 0;
          const label = field.isRequired ? `${field.name} *` : field.name;
          const multiSelectedValues = aspectValue
            ? aspectValue.split(", ").map((value) => value.trim()).filter((value) => value.length > 0)
            : [];

          return (
            <div key={field.name} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-500">{label}</label>
              {hasOptions && field.isMulti ? (
                <ComboBoxMultipleInput
                  key={`${field.name}-${aspectValue}`}
                  size="sm"
                  options={field.options}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: string) => {
                    saveAspectValue(field, value ?? "");
                  }}
                  registerName={field.name}
                  initialValue={multiSelectedValues.join(", ")}
                />
              ) : hasOptions ? (
                <ComboBoxInput
                  key={`${field.name}-${aspectValue}`}
                  size="sm"
                  options={field.options}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: any) => {
                    saveAspectValue(field, value?.localizedValue ?? "");
                  }}
                  registerName={field.name}
                  initialValue={aspectValue}
                  placeholder={t("Select an option")}
                  classes="min-w-52"
                />
              ) : (
                <input
                  type="text"
                  className="px-2 py-1 text-sm rounded border border-gray-300"
                  defaultValue={aspectValue}
                  maxLength={50}
                  placeholder={t("Enter value")}
                  onBlur={(e) => saveAspectValue(field, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
